// API 基础配置
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api'

// 需要认证的 API 路径（精确匹配前缀，避免误匹配）
const AUTH_REQUIRED_PATHS = [
  '/homework', '/mistakes', '/track', '/ai', '/orders', '/favorites',
  '/learn/progress', '/learn/my-courses', '/learn/history', '/learn/notes',
  '/user/profile'
]

// 请求拦截器
const requestInterceptor = (config) => {
  const token = localStorage.getItem('edu_token')
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  } else {
    // 未登录时，对需要认证的 API 直接拒绝，避免触发 401 全局事件
    const url = config._url || ''
    if (AUTH_REQUIRED_PATHS.some(p => url.startsWith(p))) {
      return { ...config, _skipAuth: true }
    }
  }
  return config
}

// 响应拦截器
const responseInterceptor = async (response) => {
  // 处理未授权 — 用自定义事件通知 React 层面处理，避免整页刷新
  if (response.status === 401) {
    // 只有之前有 token 的才触发过期事件（真正的 token 过期）
    const hadToken = !!localStorage.getItem('edu_token')
    localStorage.removeItem('edu_token')
    localStorage.removeItem('edu_user')
    if (hadToken) {
      // 发出自定义事件，让 AppContext 监听处理
      window.dispatchEvent(new CustomEvent('auth:expired'))
    }
    return Promise.reject(new Error('登录已过期，请重新登录'))
  }
  
  // 处理其他错误
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: '请求失败' }))
    return Promise.reject(new Error(error.message || `HTTP ${response.status}`))
  }
  
  return response.json()
}

// 基础请求方法
async function request(url, options = {}) {
  const config = requestInterceptor({
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    _url: url,  // 传给拦截器用于判断认证路径
  })

  // 未登录且访问需要认证的 API：直接返回空结果，不发请求
  if (config._skipAuth) {
    return { list: [], message: '未登录' }
  }

  // 清理内部标记
  delete config._url
  delete config._skipAuth

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, config)
    return responseInterceptor(response)
  } catch (error) {
    console.error('API Error:', error)
    throw error
  }
}

// ==================== 用户相关 API ====================
export const userApi = {
  // 登录（agreedToTerms 用于新用户自动注册时确认协议）
  login: (phone, code, agreedToTerms) => request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ phone, code, agreedToTerms }),
  }),

  // 注册
  register: (phone, code, password, agreedToTerms) => request('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ phone, code, password, agreedToTerms }),
  }),

  // 发送验证码
  sendCode: (phone) => request('/auth/code', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  }),

  // 微信登录
  wxLogin: (code) => request('/auth/wxlogin', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),

  // 绑定手机号（微信登录后）
  bindPhone: (phone, code) => request('/user/bind-phone', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  }),

  // 获取用户信息
  getProfile: () => request('/user/profile'),

  // 更新用户信息
  updateProfile: (data) => request('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 退出登录
  logout: () => request('/auth/logout', { method: 'POST' }),

  // 注销账号
  deleteAccount: () => request('/user/delete-account', { method: 'POST' }),

  // 导出个人数据
  exportData: () => request('/user/export-data'),

  // 钱包
  getWallet: () => request('/user/wallet'),
}

// ==================== 课程相关 API ====================
export const courseApi = {
  // 获取课程列表
  getList: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/courses?${query}`)
  },

  // 获取课程详情
  getDetail: (id) => request(`/courses/${id}`),

  // 获取课程章节
  getChapters: (id) => request(`/courses/${id}/chapters`),

  // 获取推荐课程
  getRecommend: () => request('/courses/recommend'),

  // 搜索课程
  search: (keyword) => request(`/courses/search?q=${encodeURIComponent(keyword)}`),

  // 获取课程分类
  getCategories: () => request('/courses/categories'),

  // 获取 Banner 列表
  getBanners: () => request('/courses/banners'),
}

// ==================== 学习相关 API ====================
export const learnApi = {
  // 获取学习进度
  getProgress: (courseId) => request(`/learn/progress/${courseId}`),

  // 更新学习进度
  updateProgress: (courseId, chapterId, progress) => request('/learn/progress', {
    method: 'POST',
    body: JSON.stringify({ courseId, chapterId, progress }),
  }),

  // 获取我的课程
  getMyCourses: () => request('/learn/my-courses'),

  // 获取学习记录
  getHistory: () => request('/learn/history'),

  // 获取学习统计（连续天数、本周数据、今日时长等）
  getStats: () => request('/learn/stats'),

  // 添加笔记
  addNote: (courseId, chapterId, content, time) => request('/learn/notes', {
    method: 'POST',
    body: JSON.stringify({ courseId, chapterId, content, time }),
  }),

  // 获取笔记列表
  getNotes: (courseId) => request(`/learn/notes/${courseId}`),

  // 获取所有笔记
  getAllNotes: () => request('/learn/notes'),

  // 删除笔记
  deleteNote: (id) => request(`/learn/notes/${id}`, { method: 'DELETE' }),

  // 每日打卡
  checkIn: () => request('/learn/checkin', {
    method: 'POST',
  }),

  // 获取仪表盘聚合数据
  getDashboard: () => request('/learn/dashboard'),
}

// ==================== 收藏相关 API ====================
export const favoriteApi = {
  // 获取收藏列表
  getList: () => request('/favorites'),

  // 添加收藏
  add: (courseId) => request('/favorites', {
    method: 'POST',
    body: JSON.stringify({ courseId }),
  }),

  // 取消收藏
  remove: (courseId) => request(`/favorites/${courseId}`, {
    method: 'DELETE',
  }),

  // 检查是否已收藏
  check: (courseId) => request(`/favorites/check/${courseId}`),
}

// ==================== 作业相关 API ====================
export const homeworkApi = {
  // 获取作业列表
  getList: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/homework?${query}`)
  },

  // 获取作业详情
  getDetail: (homeworkId) => request(`/homework/${homeworkId}`),

  // 获取作业统计
  getStats: () => request('/homework/stats'),

  // 老师数据面板
  getTeacherDashboard: () => request('/homework/teacher-dashboard'),

  // 创建作业（老师）
  create: (data) => request('/homework', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // 删除作业（老师）
  remove: (homeworkId) => request(`/homework/${homeworkId}`, {
    method: 'DELETE',
  }),

  // 学生提交作业
  submit: (homeworkId, data) => request(`/homework/${homeworkId}/submit`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // 老师批改作业
  grade: (homeworkId, submissionId, data) => request(`/homework/${homeworkId}/submissions/${submissionId}/grade`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
}

// ==================== 错题本相关 API ====================
export const mistakeApi = {
  // 获取错题列表
  getList: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/mistakes?${query}`)
  },

  // 获取错题详情
  getDetail: (mistakeId) => request(`/mistakes/${mistakeId}`),

  // 标记已掌握 / 取消掌握
  toggleMastered: (mistakeId) => request(`/mistakes/${mistakeId}/toggle-mastered`, {
    method: 'POST',
  }),

  // 更新错因标签
  updateTags: (mistakeId, data) => request(`/mistakes/${mistakeId}/tags`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // 删除错题
  remove: (mistakeId) => request(`/mistakes/${mistakeId}`, { method: 'DELETE' }),

  // 获取薄弱知识点
  getWeakPoints: () => request('/mistakes/weak-points'),
}

// ==================== 学习轨迹相关 API ====================
export const trackApi = {
  // 获取学习时间线
  getTimeline: (limit = 30) => request(`/track/timeline?limit=${limit}`),
  // 获取成绩趋势
  getScoreTrend: (subject = '') => {
    const q = subject ? `?subject=${subject}` : ''
    return request(`/track/trend${q}`)
  },
  // 获取学习统计
  getStats: () => request('/track/stats'),
}

// ==================== AI 推荐相关 API ====================
export const aiApi = {
  // 获取 AI 个性化推荐
  getRecommendation: () => request('/ai/recommend'),
  // AI 错题解析
  analyzeMistake: (mistakeId) => request(`/ai/analyze/${mistakeId}`),
  // 检查 AI 服务状态
  checkStatus: () => request('/ai/status'),
  // AI 对话
  chat: (message, requestId) => request('/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ message, requestId }),
  }),
}

// ==================== 版本检查 API ====================
export const versionApi = {
  // 检查最新版本
  check: () => request('/version'),
}

// ==================== 钱包相关 API ====================
export const walletApi = {
  getBalance: () => request('/wallet/balance'),
  getRecords: (type = '', page = 1) => {
    const q = new URLSearchParams({ type, page: String(page) }).toString()
    return request(`/wallet/records?${q}`)
  },
  recharge: (amount) => request('/wallet/recharge', {
    method: 'POST',
    body: JSON.stringify({ amount }),
  }),
  mockPay: (orderId, pin) => request('/wallet/mock-pay', {
    method: 'POST',
    body: JSON.stringify({ orderId, pin }),
  }),
  checkPin: () => request('/wallet/pin/status'),
  setPin: (oldPin, newPin) => request('/wallet/pin', {
    method: 'POST',
    body: JSON.stringify({ oldPin, newPin }),
  }),
}

// ==================== 知识点 API ====================
export const knowledgeApi = {
  // 获取知识点列表（支持筛选）
  getList: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return request(`/knowledge?${query}`)
  },
  // 获取知识点详情
  getDetail: (id) => request(`/knowledge/${encodeURIComponent(id)}`),
  // 获取统计信息
  getStats: () => request('/knowledge/stats'),
}

// 导出所有 API
export default {
  user: userApi,
  course: courseApi,
  learn: learnApi,
  favorite: favoriteApi,
  homework: homeworkApi,
  mistake: mistakeApi,
  track: trackApi,
  ai: aiApi,
  version: versionApi,
  knowledge: knowledgeApi,
}

// 模拟 API（用于开发测试）
export const mockApi = {
  // 模拟登录
  mockLogin: async (phone) => {
    await new Promise(r => setTimeout(r, 1000))
    return {
      token: 'mock_token_' + Date.now(),
      user: {
        id: 'user_' + Date.now(),
        name: '学习达人',
        phone,
        avatar: `https://api.dicebear.com/8.x/avataaars/svg?seed=${phone}`,
        level: 5,
        streak: 12,
        totalHours: 128,
      }
    }
  },

  // 模拟发送验证码
  mockSendCode: async (phone) => {
    await new Promise(r => setTimeout(r, 500))
    console.log(`验证码已发送至 ${phone}: 123456`)
    return { success: true }
  },

  // 模拟获取课程列表
  mockGetCourses: async () => {
    await new Promise(r => setTimeout(r, 500))
    return { 
      list: [
        { id: 1, title: 'Python 编程入门', price: 99, originalPrice: 199 },
        { id: 2, title: 'React 前端开发', price: 129, originalPrice: 299 },
      ]
    }
  },
}
